import React from 'react';
import styled from 'styled-components';
import { LuClock, LuPhoneCall } from 'react-icons/lu';

const InfoCardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const InfoCard = styled.div`
  background: ${props => props.theme.colors.white};
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 480px) {
    padding: 15px;
  }
`;

const InfoCardTitle = styled.h4`
  color: ${props => props.theme.colors.primary};
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
  line-height: 1.4;
  
  svg {
    color: ${props => props.theme.colors.secondary};
    flex-shrink: 0;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const InfoCardList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const InfoCardItem = styled.li`
  padding: 8px 0;
  color: ${props => props.theme.colors.grayDark};
  font-size: 14px;
  border-bottom: 1px solid #f1f5f9;
  line-height: 1.5;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  &:last-child {
    border-bottom: none;
  }
  
  strong {
    font-weight: 600;
    color: ${props => props.theme.colors.grayDark};
  }
  
  @media (max-width: 480px) {
    font-size: 13px;
    padding: 6px 0;
  }
`;

const CoverageInfo = ({ cliente }) => {
  const [phones, setPhones] = React.useState([]);
  const [carencias, setCarencias] = React.useState([]);
  
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { paginaAPI } = await import('../services/api');
        const resp = await paginaAPI.getDashboard();
        const list = resp?.data?.data?.telefones || [];
        if (mounted) setPhones(list);
      } catch (e) {
        // silencioso: mantém conteúdo estático se necessário
      }
    })();
    return () => { mounted = false; };
  }, []);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { paginaAPI } = await import('../services/api');
        const resp = await paginaAPI.getCobertura();
        const carenciaList = resp?.data?.data?.condicoes_carencias?.carencia || [];
        if (mounted) setCarencias(carenciaList);
      } catch (e) {
        // silencioso: mantém conteúdo estático se necessário
      }
    })();
    return () => { mounted = false; };
  }, []);

  const renderTextWithBold = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    if (typeof item === 'object' && item !== null) {
      return (
        <>
          {item.texto || ''}
          {item.negrito && Array.isArray(item.negrito) && item.negrito.length > 0 && item.negrito.map((boldText, idx) => (
            <strong key={`bold-${idx}`}>{boldText}</strong>
          ))}
          {item.complemento || ''}
        </>
      );
    }
    return String(item);
  };

  const calculateCarencia = (idade) => {
    if (!idade) return 'N/A';
    const ageNum = parseInt(idade);
    return ageNum >= 60 ? '120 dias (4 meses)' : '60 dias (2 meses)';
  };

  const clientAge = cliente?.idade ? cliente.idade : (cliente?.dataNascimento ? (() => {
    // Parse da data de nascimento considerando timezone local
    let birth;
    const dateStr = cliente.dataNascimento;
    
    if (typeof dateStr === 'string' && dateStr.includes('-')) {
      // Formato ISO (YYYY-MM-DD)
      const datePart = dateStr.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      birth = new Date(year, month - 1, day);
    } else if (typeof dateStr === 'string' && dateStr.includes('/')) {
      // Formato DD/MM/YYYY
      const [day, month, year] = dateStr.split('/').map(Number);
      birth = new Date(year, month - 1, day);
    } else {
      birth = new Date(dateStr);
    }
    
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  })() : null);

  return (
    <InfoCardsContainer>
      <InfoCard>
        <InfoCardTitle>
          <LuClock size={18} />
          Período de Carência
        </InfoCardTitle>
        <InfoCardList>
          {carencias && carencias.length > 0 ? (
            carencias.map((item, idx) => (
              <InfoCardItem key={`caren-${idx}`}>
                {renderTextWithBold(item)}
              </InfoCardItem>
            ))
          ) : (
            <>
              <InfoCardItem><strong>Até 59 anos:</strong> 60 dias (2 meses)</InfoCardItem>
              <InfoCardItem><strong>60 anos ou mais:</strong> 120 dias (4 meses)</InfoCardItem>
              <InfoCardItem>
                <strong>Plano PET:</strong> 90 dias (0-7 anos) / 180 dias (8+ anos)
              </InfoCardItem>
            </>
          )}
        </InfoCardList>
      </InfoCard>
      
      <InfoCard>
        <InfoCardTitle>
          <LuPhoneCall size={18} />
          Atendimento
        </InfoCardTitle>
        <InfoCardList>
          {phones && phones.length > 0 ? (
            phones.map((p, i) => {
              const isZap = String(p.tipo).toLowerCase() === 'whatsapp';
              const href = isZap ? `https://wa.me/55${String(p.numero).replace(/\D/g,'')}` : `tel:+55${String(p.numero).replace(/\D/g,'')}`;
              return (
                <InfoCardItem key={`ph-${i}`}>
                  <strong>{isZap ? 'WhatsApp' : 'Telefone'}:</strong>{' '}
                  <a href={href} target={isZap ? '_blank' : undefined} rel={isZap ? 'noopener noreferrer' : undefined}>{p.rotulo}</a>
                </InfoCardItem>
              );
            })
          ) : (
            <>
              <InfoCardItem>
                <strong>Telefone:</strong>{' '}
                <a href="tel:+552132798800">(21) 3279-8800</a> / <a href="tel:+552123031000">(21) 2303-1000</a>
              </InfoCardItem>
              <InfoCardItem>
                <strong>WhatsApp:</strong>{' '}
                <a href="https://wa.me/558000213460" target="_blank" rel="noopener noreferrer" style={{ color: 'green', fontWeight: 'bold' }}>0800 021 3460</a>
              </InfoCardItem>
            </>
          )}
          <InfoCardItem><strong>E-mail:</strong> contato@santacasacopacabana.com.br</InfoCardItem>
          <InfoCardItem><strong>Endereço:</strong> Rua Arquias Cordeiro, 257</InfoCardItem>
          <InfoCardItem><strong>Atendimento:</strong> 24 horas</InfoCardItem>
        </InfoCardList>
      </InfoCard>
    </InfoCardsContainer>
  );
};

export default CoverageInfo;


